import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Icon } from '../../components/ui/Icon'
import { Skeleton } from '../../components/ui/Skeleton'
import { resolveSearchRoute } from './catalog-search-routing'
import { ResultCard } from './components/ResultCard'
import { defaultCatalogSearchService, useCatalogSearch } from './useCatalogSearch'
import type { CatalogSearchService } from './useCatalogSearch'

export interface CatalogSearchPageProps {
  /** Injectable for tests (mirrors every other vertical page's `service`
   * prop, e.g. `PartsLibraryPage`); defaults to the real BFF-backed service. */
  readonly service?: CatalogSearchService
}

/**
 * The top bar's search destination (UID-251), reached by submitting
 * `/catalog/search?q=...` — see `TopBar.tsx`. There is no on-page search box
 * here: per the design source, search lives in the top bar only (mirrors
 * `CatalogBrowsePage`'s own precedent of dropping its search header for the
 * same reason).
 *
 * Per README §7 screen 3 / §8 "Search routing" (2026-08-23 design refresh,
 * authoritative): submitting a search must NEVER render the legacy paginated
 * results grid this screen used to show (UID-32/UID-227). Instead, once the
 * keyword search resolves, this screen routes onward (see
 * `catalog-search-routing.ts`'s `resolveSearchRoute`):
 *   - an exact (or, failing that, substring) match on a returned card's
 *     `catalogSku`, for a query that looks like a part number, deep-links
 *     straight to that card's series page (`CatalogSeriesConfigurePage`, the
 *     real per-series "parts table" — ADR-0097/0098/0099) when the card
 *     carries a `seriesCode`;
 *   - an exact match with no `seriesCode` (no linked `catalog_series` row —
 *     ~25 business-critical MISUMI SKUs plus ~31k other seed rows have no
 *     family to route to) stays right here and renders just that one matched
 *     card via `ResultCard` (mirrors the design source's own `pnLookup`/
 *     `runSearch` fallback) — never a category-wide grid for a part the user
 *     asked for by exact SKU;
 *   - otherwise, any match at all navigates to `/catalog/series/:category`
 *     (`CatalogSeriesPage`, the "new category selector," UID-286) for
 *     whichever category the match(es) belong to;
 *   - a true no-match still runs the search and shows the no-results state.
 * There is deliberately no inline flat listing/table rendered on this screen
 * for any of these besides that single matched-card case above — the
 * architecture record
 * (`docs/design/catalog-configurator-surfaces-reconciliation.md` §0) found no
 * flat multi-result listing screen in the design source; building one reads
 * as a finished screen it isn't. `ResultCard` itself is explicitly reserved
 * for this screen already (see `CatalogBrowsePage.tsx`'s doc comment). The
 * sourcing-agent follow-up (UID-36) now covers the no-match "ask the
 * sourcing agent" action described here.
 */
export function CatalogSearchPage({
  service = defaultCatalogSearchService,
}: CatalogSearchPageProps) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const query = searchParams.get('q')?.trim() ?? ''
  const { state, search } = useCatalogSearch(service)
  const searchRef = useRef(search)
  searchRef.current = search
  // Tracks the last query this screen has already run a search for, so the
  // effect below doesn't re-run `search` on every render — only when the
  // URL's `q` actually changes (e.g. a fresh top-bar submission while
  // already on this page).
  const lastRunQueryRef = useRef<string | null>(null)
  // Tracks the last query this screen has already made a routing decision
  // for, so the routing effect fires exactly once per search — without it,
  // re-renders while `navigate()` is settling would re-evaluate (and
  // potentially re-navigate for) the same still-current results.
  const routedQueryRef = useRef<string | null>(null)

  useEffect(() => {
    if (query.length === 0 || lastRunQueryRef.current === query) return
    lastRunQueryRef.current = query
    routedQueryRef.current = null
    searchRef.current(query)
  }, [query])

  // The routing decision (README §8 "Search routing") — see the component
  // doc comment above. Computed once per ready result set so both the
  // navigation effect below and the render logic agree on it.
  const route = useMemo(
    () => (state.status === 'ready' ? resolveSearchRoute(query, state.results.items) : null),
    [state, query],
  )

  useEffect(() => {
    if (state.status !== 'ready' || routedQueryRef.current === query) return
    routedQueryRef.current = query
    // `kind: 'part'` deliberately does NOT navigate — see the doc comment
    // above; the matched card renders in place below instead.
    if (!route || route.kind === 'part') return
    const category = encodeURIComponent(route.category)
    navigate(
      route.kind === 'series'
        ? `/catalog/series/${category}/${encodeURIComponent(route.seriesCode)}`
        : `/catalog/series/${category}`,
    )
  }, [state, query, navigate, route])

  const isRoutingElsewhere = route?.kind === 'series' || route?.kind === 'category'
  const matchedPartCard =
    route?.kind === 'part' && state.status === 'ready'
      ? (state.results.items.find((item) => item.sourceable.catalogSku === route.sku) ?? null)
      : null

  return (
    <section className="catalog-storefront" aria-labelledby="catalog-search-heading">
      <div className="catalog-series-breadcrumb">
        <Link to="/catalog">Catalog</Link>
        <span aria-hidden="true">/</span>
        <span>Search</span>
      </div>

      <h1 id="catalog-search-heading" className="catalog-storefront-title">
        {query.length > 0 ? `Results for "${query}"` : 'Search the catalog'}
      </h1>

      {query.length === 0 && (
        <EmptyState
          icon={<Icon name="search" />}
          title="Search the catalog"
          detail="Use the search field in the top bar to look up a part number or keyword."
        />
      )}

      {(state.status === 'loading' || isRoutingElsewhere) && (
        <output className="catalog-result-grid" aria-busy="true">
          <span className="sr-only">
            {state.status === 'loading' ? 'Searching…' : 'Redirecting to the best match…'}
          </span>
          {Array.from({ length: 6 }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-count skeleton row has no stable identity to key by.
            <Skeleton key={index} height={180} variant="block" />
          ))}
        </output>
      )}

      {matchedPartCard && (
        <output className="catalog-result-grid" aria-live="polite">
          <span className="sr-only">Showing the exact match for "{query}"</span>
          <ResultCard card={matchedPartCard} />
        </output>
      )}

      {state.status === 'error' && (
        <EmptyState
          title="We couldn’t search the catalog"
          detail={state.message}
          action={<Button onClick={() => search(query)}>Try again</Button>}
        />
      )}

      {state.status === 'ready' && state.results.items.length === 0 && (
        <EmptyState
          icon={<Icon name="search" />}
          title={`No catalog matches for "${query}"`}
          detail="We searched the MISUMI catalog and your Fictiv library and couldn't find an off-the-shelf part. Try a broader term, browse by category, or ask the sourcing agent to dig deeper."
          action={
            <div className="catalog-search-empty-actions">
              <Button onClick={() => navigate('/catalog')}>Browse categories</Button>
              {/* UID-36 (README §7 screen 2 / §13 Epic C1): hands the query off
               * to the sourcing-agent thread (`AgentSearchPage`), mirroring
               * the design source's `onCatAskAgent` -> `askAgent(catQuery)`. */}
              <Button
                variant="outline"
                onClick={() => navigate(`/agent?q=${encodeURIComponent(query)}`)}
              >
                Ask the sourcing agent
              </Button>
            </div>
          }
        />
      )}
    </section>
  )
}
